import { type SunMoment, momentAt } from '../palette';

export interface SandPalette {
  sable: string;
  sableClair: string;
  ride: string;
}

// Replaces sol and solClair, on the Dome's ground only.
export const SAND_PALETTES: Record<SunMoment, SandPalette> = {
  crepuscule: { sable: '#55363c', sableClair: '#74484a', ride: '#7a4a52' },
  nuit: { sable: '#211b2e', sableClair: '#322840', ride: '#463a5a' },
  aube: { sable: '#e2c39c', sableClair: '#efd7b2', ride: '#c9a47a' },
  jour: { sable: '#ebd3a0', sableClair: '#f6e5bf', ride: '#d2b07c' },
};

export interface HillPalette {
  herbe: string;
  herbeClaire: string;
  sable: string;
  sableClair: string;
  courbe: string;
  arbre: string;
  arbreClair: string;
  coussin: string;
  soleil: string;
  // How strongly the east light, in `soleil`, washes the hill.
  est: number;
}

// Replaces sol and solClair, on the Ambyss hill only.
export const HILL_PALETTES: Record<SunMoment, HillPalette> = {
  crepuscule: {
    herbe: '#3a2236',
    herbeClaire: '#4d2d45',
    sable: '#5c3a44',
    sableClair: '#7a525a',
    courbe: '#7a4a5c',
    arbre: '#2a1c2c',
    arbreClair: '#4a3042',
    coussin: '#e0485a',
    soleil: '#ff9a6a',
    est: 0,
  },
  nuit: {
    herbe: '#0a1226',
    herbeClaire: '#131f3e',
    sable: '#1d1932',
    sableClair: '#2b2547',
    courbe: '#2c3c6c',
    arbre: '#0f2a2c',
    arbreClair: '#1c4644',
    coussin: '#b23048',
    soleil: '#9b6bff',
    est: 0.18,
  },
  aube: {
    herbe: '#cdb39e',
    herbeClaire: '#dcc3ad',
    sable: '#e8d0b4',
    sableClair: '#f5e4cc',
    courbe: '#b2937c',
    arbre: '#6c7a58',
    arbreClair: '#8c9a70',
    coussin: '#c43a40',
    soleil: '#ffd8a0',
    est: 0.85,
  },
  jour: {
    herbe: '#d6cb98',
    herbeClaire: '#e3d9aa',
    sable: '#eedbae',
    sableClair: '#f8ecca',
    courbe: '#b4a476',
    arbre: '#5e7a44',
    arbreClair: '#7e9a5a',
    coussin: '#c8323a',
    soleil: '#fff4d0',
    est: 0.4,
  },
};

export interface CanopyPalette {
  toile: string;
  corde: string;
  violet: string;
}

export const CANOPY_PALETTES: Record<SunMoment, CanopyPalette> = {
  crepuscule: { toile: '#c88a5a', corde: '#e0b090', violet: '#9b6bff' },
  nuit: { toile: '#6a4a8a', corde: '#8a7aa8', violet: '#9b6bff' },
  aube: { toile: '#e2a66a', corde: '#8a6a50', violet: '#6a3fc0' },
  jour: { toile: '#e0a060', corde: '#8a6a48', violet: '#6a3fc0' },
};

export interface DesertPalette {
  sable: string;
  ride: string;
  crete: string;
  abri: string;
  garance: string;
  indigo: string;
  safran: string;
  ocre: string;
  creme: string;
  trait: string;
  rosace: string;
  // Share of the rug colour mixed over the sand, from 0 to 1.
  tapis: number;
}

export const DESERT_PALETTES: Record<SunMoment, DesertPalette> = {
  crepuscule: {
    sable: '#4e3040',
    ride: '#6a4252',
    crete: '#8a5c62',
    abri: '#2e1a2a',
    garance: '#9a2c36',
    indigo: '#2c3274',
    safran: '#d0903e',
    ocre: '#a06a2a',
    creme: '#ecd0a0',
    trait: '#f2d2a0',
    rosace: '#d9a441',
    tapis: 0.7,
  },
  nuit: {
    sable: '#1c1834',
    ride: '#2b2550',
    crete: '#3b3368',
    abri: '#110e22',
    garance: '#722632',
    indigo: '#28306c',
    safran: '#bf8a42',
    ocre: '#7a5222',
    creme: '#e2c08a',
    trait: '#e8c890',
    rosace: '#d9a441',
    tapis: 0.62,
  },
  aube: {
    sable: '#e2bc90',
    ride: '#c89a6a',
    crete: '#f4dab0',
    abri: '#b8885c',
    garance: '#a8322c',
    indigo: '#2e4a9a',
    safran: '#e2a03a',
    ocre: '#c9822e',
    creme: '#f6e6c4',
    trait: '#fff1d6',
    rosace: '#a8781f',
    tapis: 0.85,
  },
  jour: {
    sable: '#eac792',
    ride: '#d4a466',
    crete: '#f8e0b0',
    abri: '#c48f52',
    garance: '#a8322c',
    indigo: '#2e4a9a',
    safran: '#e2a03a',
    ocre: '#c9822e',
    creme: '#f6e6c4',
    trait: '#fff1d6',
    rosace: '#a8781f',
    tapis: 0.88,
  },
};

export function desertAt(fraction: number): DesertPalette {
  return momentAt(DESERT_PALETTES, fraction);
}

export function sandAt(fraction: number): SandPalette {
  return momentAt(SAND_PALETTES, fraction);
}

export function canopyAt(fraction: number): CanopyPalette {
  return momentAt(CANOPY_PALETTES, fraction);
}

export function hillAt(fraction: number): HillPalette {
  return momentAt(HILL_PALETTES, fraction);
}

export interface TentPalette {
  toile: string;
  toileAlpha: number;
  pic: string;
  picAlpha: number;
  bande: string;
  bandeAlpha: number;
  couture: string;
  bord: string;
  creme: string;
  indigo: string;
  corde: string;
  piquet: string;
  laiton: string;
  verre: string;
  flamme: string;
  lueur: string;
  lueurAlpha: number;
  bois: string;
  boisTrait: string;
  boisAlpha: number;
  pouf: string;
  poufTrait: string;
}

export const TENT_PALETTES: Record<SunMoment, TentPalette> = {
  crepuscule: {
    toile: '#ff8c42',
    toileAlpha: 0.11,
    pic: '#ffbc78',
    picAlpha: 0.22,
    bande: '#d8404e',
    bandeAlpha: 0.06,
    couture: '#e8985c',
    bord: '#c43a48',
    creme: '#f4d8b0',
    indigo: '#4c5cc4',
    corde: '#a07a98',
    piquet: '#c4a0b8',
    laiton: '#f0b850',
    verre: '#38bcbc',
    flamme: '#ffe8c4',
    lueur: '#ffa448',
    lueurAlpha: 0.13,
    bois: '#98683c',
    boisTrait: '#d8a058',
    boisAlpha: 0.4,
    pouf: '#925c34',
    poufTrait: '#e0a868',
  },
  nuit: {
    toile: '#ff9a48',
    toileAlpha: 0.1,
    pic: '#ffb36a',
    picAlpha: 0.22,
    bande: '#e0485a',
    bandeAlpha: 0.05,
    couture: '#ffb070',
    bord: '#c43c46',
    creme: '#f2d6a8',
    indigo: '#5a6ad8',
    corde: '#8f7aa8',
    piquet: '#b9a6d0',
    laiton: '#ffcf6a',
    verre: '#3fd0c9',
    flamme: '#fff4d6',
    lueur: '#ffae4a',
    lueurAlpha: 0.22,
    bois: '#a0703f',
    boisTrait: '#e0b060',
    boisAlpha: 0.35,
    pouf: '#8a5a32',
    poufTrait: '#e8b070',
  },
  aube: {
    toile: '#f49846',
    toileAlpha: 0.17,
    pic: '#ffd8a0',
    picAlpha: 0.34,
    bande: '#b83438',
    bandeAlpha: 0.065,
    couture: '#c06e2c',
    bord: '#b4323e',
    creme: '#fff0d8',
    indigo: '#3a50a8',
    corde: '#8a6a52',
    piquet: '#664632',
    laiton: '#a87420',
    verre: '#25868e',
    flamme: '#c09040',
    lueur: '#ffae4a',
    lueurAlpha: 0.04,
    bois: '#8e643a',
    boisTrait: '#684424',
    boisAlpha: 0.46,
    pouf: '#a86e3e',
    poufTrait: '#74441e',
  },
  jour: {
    toile: '#f0a040',
    toileAlpha: 0.2,
    pic: '#ffe0a8',
    picAlpha: 0.4,
    bande: '#b0302a',
    bandeAlpha: 0.07,
    couture: '#b8621c',
    bord: '#b0302a',
    creme: '#fff4dc',
    indigo: '#2e4a9a',
    corde: '#8a6a4a',
    piquet: '#5c3e22',
    laiton: '#9a6a18',
    verre: '#1f7a86',
    flamme: '#9a6a18',
    lueur: '#ffae4a',
    lueurAlpha: 0,
    bois: '#8a6038',
    boisTrait: '#5a3a1c',
    boisAlpha: 0.5,
    pouf: '#b07440',
    poufTrait: '#6a3e18',
  },
};

export function tentAt(fraction: number): TentPalette {
  return momentAt(TENT_PALETTES, fraction);
}
